import { Link } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPost } from '../lib/api.ts'
import { useSelectedCampaign } from '../lib/campaign.tsx'

interface SavedUniverse {
  universe_id: string
  name: string
  description: string | null
  version_no: number | null
  row_count: string | number | null
  counted_at: string | null
  updated_at: string
  created_by_email: string | null
}

export function UniverseList() {
  const { campaignId, campaign } = useSelectedCampaign()
  const client = useQueryClient()

  const { data, isPending } = useQuery({
    queryKey: ['universes', campaignId],
    queryFn: () => apiGet<{ universes: SavedUniverse[] }>(`/universes?campaign_id=${campaignId}`),
    enabled: Boolean(campaignId),
  })

  async function archive(universe: SavedUniverse) {
    if (!confirm(`Archive “${universe.name}”? It stays queryable for existing exports.`)) return
    await apiPost(`/universes/${universe.universe_id}/archive`)
    await client.invalidateQueries({ queryKey: ['universes', campaignId] })
  }

  const universes = data?.universes ?? []

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Universes</h1>
          <p className="mt-1 text-sm text-ink-muted">
            Saved filter definitions. Each save keeps its own version.
          </p>
        </div>
      </div>

      <div className="mt-5 flex items-center gap-3">
        {campaign?.can_build_universes ? (
          <Link
            to="/universes/new"
            className="rounded-md bg-brand-900 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-800"
          >
            New universe
          </Link>
        ) : campaign?.can_run_counts ? (
          // A Worker can still reach the builder to explore counts; the save
          // button is disabled there rather than the whole screen withheld.
          <>
            <Link
              to="/universes/new"
              className="rounded-md border border-line px-3 py-1.5 text-sm font-medium text-ink hover:border-brand-400"
            >
              Run a count
            </Link>
            <p className="text-sm text-ink-muted">Saving a universe needs a Campaign Manager role.</p>
          </>
        ) : null}
      </div>

      <div className="mt-5 overflow-x-auto">
        <table className="w-full min-w-[40rem] border-collapse text-sm">
          <thead>
            <tr className="border-b border-line text-left text-xs tracking-wide text-ink-muted uppercase">
              <th scope="col" className="py-2 pr-4 font-semibold">Name</th>
              <th scope="col" className="py-2 pr-4 font-semibold">Version</th>
              <th scope="col" className="py-2 pr-4 font-semibold">Voters when saved</th>
              <th scope="col" className="py-2 pr-4 font-semibold">Updated</th>
              <th scope="col" className="relative py-2 font-semibold"><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {isPending ? (
              <tr><td colSpan={5} className="py-4 text-ink-muted">Loading…</td></tr>
            ) : universes.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-6 text-ink-muted">
                  No universes yet on this campaign.
                </td>
              </tr>
            ) : (
              universes.map((u) => (
                <tr key={u.universe_id} className="border-b border-line/60">
                  <td className="py-2 pr-4">
                    <Link
                      to="/universes/$universeId"
                      params={{ universeId: u.universe_id }}
                      className="font-medium text-ink underline-offset-2 hover:underline"
                    >
                      {u.name}
                    </Link>
                    {u.description ? (
                      <div className="text-xs text-ink-muted">{u.description}</div>
                    ) : null}
                  </td>
                  <td className="py-2 pr-4 tabular-nums text-ink-muted">v{u.version_no ?? 1}</td>
                  <td className="py-2 pr-4 font-mono tabular-nums">
                    {u.row_count === null ? (
                      '—'
                    ) : (
                      <>
                        {Number(u.row_count).toLocaleString()}
                        {/* A count is a fact about a moment, not a property of
                            the universe, so it never appears without its date. */}
                        {u.counted_at ? (
                          <span className="ml-1 font-sans text-xs text-ink-muted">
                            as of {u.counted_at.slice(0, 10)}
                          </span>
                        ) : null}
                      </>
                    )}
                  </td>
                  <td className="py-2 pr-4 text-ink-muted">{u.updated_at.slice(0, 10)}</td>
                  <td className="py-2 text-right">
                    {campaign?.can_build_universes ? (
                      <button
                        type="button"
                        onClick={() => archive(u)}
                        className="text-xs text-ink-muted hover:text-ink"
                      >
                        Archive
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
